import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../lib/api';
import { fmtHours, fmtDuration } from '../lib/formatters';
import { useAuth } from '../hooks/useAuth';
import {
  FolderOpen,
  Plus,
  X,
  Users,
  ChevronRight,
  Archive,
  ArchiveRestore,
  Trash2,
  Edit2,
  Search,
  Clock,
  CheckCircle2,
  Circle,
  ArrowRight,
  UserMinus,
  UserPlus,
  Play,
  Activity,
} from 'lucide-react';

// ---- schemas ----
const projectSchema = z.object({
  name: z.string().min(1, 'Project name is required'),
  description: z.string().optional(),
  budget: z
    .string()
    .optional()
    .refine(
      (v) => !v || (!isNaN(Number(v)) && Number(v) >= 0),
      'Must be positive'
    ),
});

const taskSchema = z.object({
  title: z.string().min(1, 'Task title is required'),
  assignedTo: z.string().optional().nullable(),
});

// ---- helpers ----
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function Avatar({ name, size = 'sm', colorIndex }) {
  const colors = [
    'bg-blue-100 text-blue-700',
    'bg-purple-100 text-purple-700',
    'bg-green-100 text-green-700',
    'bg-orange-100 text-orange-700',
    'bg-pink-100 text-pink-700',
    'bg-teal-100 text-teal-700',
  ];
  const idx = colorIndex !== undefined ? colorIndex % colors.length : (name?.charCodeAt(0) || 0) % colors.length;
  const sizeClass = size === 'sm' ? 'w-6 h-6 text-xs' : size === 'md' ? 'w-8 h-8 text-sm' : 'w-10 h-10 text-base';
  return (
    <div
      className={`rounded-full font-semibold flex items-center justify-center border-2 border-white flex-shrink-0 ${sizeClass} ${colors[idx]}`}
      title={name}
    >
      {getInitials(name)}
    </div>
  );
}

function BurnBar({ pct }) {
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-blue-500';
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
}

function StatusBadge({ archived }) {
  return archived ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <Archive className="w-3 h-3" />
      Archived
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <Circle className="w-3 h-3 fill-green-500 text-green-500" />
      Active
    </span>
  );
}

function RoleBadge({ role }) {
  const map = {
    ADMIN: 'bg-purple-100 text-purple-700',
    MANAGER: 'bg-blue-100 text-blue-700',
    EMPLOYEE: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[role] || map.EMPLOYEE}`}>
      {role ? role.charAt(0) + role.slice(1).toLowerCase() : 'Employee'}
    </span>
  );
}

function TaskStatusBadge({ status }) {
  const map = {
    todo: 'bg-gray-100 text-gray-600',
    'in-progress': 'bg-blue-100 text-blue-700',
    done: 'bg-green-100 text-green-700',
  };
  const labels = { todo: 'Todo', 'in-progress': 'In Progress', done: 'Done' };
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${map[status] || map.todo}`}>
      {labels[status] || status}
    </span>
  );
}

function Skeleton({ className }) {
  return <div className={`bg-gray-200 rounded animate-pulse ${className}`} />;
}

// ---- project form modal ----
function ProjectFormModal({ project, onClose, onSuccess }) {
  const isEdit = !!project;
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: project?.name || '',
      description: project?.description || '',
      budget: project?.budget != null ? String(project.budget) : '',
    },
  });

  const onSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        description: values.description || '',
        budget: values.budget ? Number(values.budget) : null,
      };
      if (isEdit) {
        await api.put(`/api/projects/${project.id}`, payload);
      } else {
        await api.post('/api/projects', payload);
      }
      onSuccess();
    } catch (err) {
      setError('root', { message: err?.response?.data?.error || 'Failed to save project.' });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">
            {isEdit ? 'Edit Project' : 'New Project'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4" noValidate>
          {errors.root && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {errors.root.message}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                errors.name ? 'border-red-400 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Website Redesign"
              autoFocus
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none transition-colors"
              placeholder="Optional description..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Budget (hours)</label>
            <input
              type="number"
              min="0"
              step="0.5"
              {...register('budget')}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                errors.budget ? 'border-red-400 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="100"
            />
            {errors.budget && <p className="mt-1 text-xs text-red-600">{errors.budget.message}</p>}
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium transition-colors"
            >
              {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- inline editable project name ----
function InlineEditName({ value, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setDraft(value);
      setEditing(false);
      return;
    }
    await onSave(trimmed);
    setEditing(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') {
      setDraft(value);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        className="text-xl font-bold text-gray-900 bg-transparent border-b-2 border-blue-500 focus:outline-none w-full"
      />
    );
  }

  return (
    <h2
      className={`text-xl font-bold text-gray-900 truncate ${!disabled ? 'cursor-pointer hover:text-blue-700 transition-colors group-name' : ''}`}
      onClick={() => !disabled && setEditing(true)}
      title={!disabled ? 'Click to edit' : undefined}
    >
      {value}
      {!disabled && (
        <Edit2 className="inline w-4 h-4 ml-2 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity align-middle" />
      )}
    </h2>
  );
}

// ---- kanban task card ----
function TaskCard({ index, task, members, projectId, isEmployee, isManager, isMemberOfProject }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);

  const updateTask = useMutation({
    mutationFn: (updates) => api.patch(`/api/tasks/${task.id}`, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-tasks', projectId] }),
  });

  const deleteTask = useMutation({
    mutationFn: () => api.delete(`/api/tasks/${task.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-tasks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const assignee = members.find((m) => m.userId === task.assignedTo);
  const canEditFull = isManager;
  const canChangeStatus = isManager || isEmployee;

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`bg-white rounded-lg border border-gray-200 shadow-sm transition-all duration-150 cursor-pointer select-none mb-2 ${
            snapshot.isDragging ? 'rotate-2 shadow-xl ring-2 ring-blue-500 z-50' : 'hover:shadow-md'
          }`}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="p-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-gray-800 leading-snug flex-1">{task.title}</p>
              {isManager && hovered && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Delete this task?')) deleteTask.mutate();
                  }}
                  className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                  title="Delete task"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center justify-between mt-2 gap-2">
              {assignee ? (
                <div className="flex items-center gap-1.5">
                  <Avatar name={assignee.user?.name} size="sm" />
                  <span className="text-xs text-gray-500 truncate max-w-[80px]">{assignee.user?.name}</span>
                </div>
              ) : (
                <span className="text-xs text-gray-400 italic">Unassigned</span>
              )}
              {task.totalDuration > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-gray-400 font-medium whitespace-nowrap">
                  <Clock className="w-3 h-3" />
                  {fmtDuration(task.totalDuration)}
                </span>
              )}
            </div>
          </div>

          {expanded && (
            <div
              className="border-t border-gray-100 p-3 space-y-2"
              onClick={(e) => e.stopPropagation()}
            >
              {canEditFull && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Assignee</label>
                  <select
                    value={task.assignedTo || ''}
                    onChange={(e) => updateTask.mutate({ assignedTo: e.target.value || null })}
                    disabled={updateTask.isPending}
                    className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Unassigned</option>
                    {members.map((m) => (
                      <option key={m.user?.id} value={m.user?.id}>
                        {m.user?.name || 'Unknown'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {canChangeStatus && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {['todo', 'in-progress', 'done'].map((s) => (
                      <button
                        key={s}
                        onClick={() => updateTask.mutate({ status: s })}
                        disabled={updateTask.isPending}
                        className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                          task.status === s
                            ? s === 'todo'
                              ? 'bg-gray-200 border-gray-300 text-gray-700'
                              : s === 'in-progress'
                              ? 'bg-blue-100 border-blue-300 text-blue-700'
                              : 'bg-green-100 border-green-300 text-green-700'
                            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {s === 'todo' ? 'Todo' : s === 'in-progress' ? 'In Progress' : 'Done'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}

// ---- add task inline form ----
function AddTaskForm({ projectId, members, onDone }) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(taskSchema) });

  const onSubmit = async (values) => {
    try {
      const payload = { title: values.title, projectId, status: 'todo' };
      if (values.assignedTo) payload.assignedTo = values.assignedTo;
      await api.post('/api/tasks', payload);
      queryClient.invalidateQueries({ queryKey: ['project-tasks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      reset();
      onDone();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg space-y-2"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="text"
        {...register('title')}
        placeholder="Task title..."
        autoFocus
        className={`w-full px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
          errors.title ? 'border-red-400' : 'border-gray-300'
        }`}
      />
      {errors.title && <p className="text-xs text-red-600">{errors.title.message}</p>}
      <select
        {...register('assignedTo')}
        className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none"
      >
        <option value="">Unassigned</option>
        {members.map((m) => (
          <option key={m.user?.id} value={m.user?.id}>
            {m.user?.name || 'Unknown'}
          </option>
        ))}
      </select>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60 font-medium transition-colors"
        >
          {isSubmitting ? 'Adding...' : 'Add Task'}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="px-3 py-1.5 text-xs bg-white border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---- kanban column ----
function KanbanColumn({ title, status, tasks, members, projectId, isEmployee, isManager, isMemberOfProject, showAddTask, onToggleAddTask }) {
  const statusColors = {
    todo: 'bg-gray-100 text-gray-600',
    'in-progress': 'bg-blue-100 text-blue-700',
    done: 'bg-green-100 text-green-700',
  };
  const headerColors = {
    todo: 'border-t-gray-400',
    'in-progress': 'border-t-blue-500',
    done: 'border-t-green-500',
  };

  const canAddTask = isManager || isMemberOfProject;

  return (
    <div className={`flex flex-col bg-gray-50/50 rounded-xl border border-gray-200 border-t-2 ${headerColors[status]} min-h-[300px]`}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 bg-white/50 rounded-t-xl backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-700 uppercase tracking-tight">{title}</span>
          <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${statusColors[status]} ring-1 ring-inset ring-white/20`}>
            {tasks.length}
          </span>
        </div>
        {status === 'todo' && canAddTask && (
          <button
            onClick={onToggleAddTask}
            className="p-1 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            title="Add task"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 p-2 space-y-2 overflow-y-auto transition-colors duration-200 ${
              snapshot.isDraggingOver ? 'bg-blue-50/50 shadow-inner' : ''
            }`}
          >
            {tasks.length === 0 && !showAddTask && (
              <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">No tasks</p>
              </div>
            )}
            {tasks.map((task, index) => (
              <TaskCard
                key={task.id}
                index={index}
                task={task}
                members={members}
                projectId={projectId}
                isEmployee={isEmployee}
                isManager={isManager}
                isMemberOfProject={isMemberOfProject}
              />
            ))}
            {provided.placeholder}
            {status === 'todo' && showAddTask && (
              <AddTaskForm
                projectId={projectId}
                members={members}
                onDone={() => onToggleAddTask()}
              />
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// ---- tasks tab ----
function TasksTab({ project, isEmployee, isManager, userId }) {
  const [showAddTask, setShowAddTask] = useState(false);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['project-tasks', project.id],
    queryFn: async () => {
      const res = await api.get(`/api/tasks?projectId=${project.id}`);
      return res.data?.data || [];
    },
  });

  const isMemberOfProject = (project.members || []).some((m) => m.userId === userId);

  const byStatus = useMemo(() => ({
    todo: tasks.filter((t) => t.status === 'todo'),
    'in-progress': tasks.filter((t) => t.status === 'in-progress'),
    done: tasks.filter((t) => t.status === 'done'),
  }), [tasks]);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Trigger status update mutation
    const newStatus = destination.droppableId;
    
    // Optimistic UI update (optional, but query invalidation is safer here)
    try {
      await api.patch(`/api/tasks/${draggableId}`, { status: newStatus });
      queryClient.invalidateQueries({ queryKey: ['project-tasks', project.id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    } catch (err) {
      console.error('Drag status update failed:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4">
        {[1, 2, 3].map((n) => (
          <div key={n} className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3">
            <Skeleton className="h-5 w-24" />
            {[1, 2].map((k) => (
              <Skeleton key={k} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="p-4 h-full overflow-x-auto overflow-y-hidden">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex flex-col md:flex-row gap-6 min-w-[750px] h-full items-start">
          <div className="flex-1 w-full md:w-1/3">
            <KanbanColumn
              title="Todo"
              status="todo"
              tasks={byStatus.todo}
              members={project.members || []}
              projectId={project.id}
              isEmployee={isEmployee}
              isManager={isManager}
              isMemberOfProject={isMemberOfProject}
              showAddTask={showAddTask}
              onToggleAddTask={() => setShowAddTask((v) => !v)}
            />
          </div>
          <div className="flex-1 w-full md:w-1/3">
            <KanbanColumn
              title="In Progress"
              status="in-progress"
              tasks={byStatus['in-progress']}
              members={project.members || []}
              projectId={project.id}
              isEmployee={isEmployee}
              isManager={isManager}
              isMemberOfProject={isMemberOfProject}
              showAddTask={false}
              onToggleAddTask={() => {}}
            />
          </div>
          <div className="flex-1 w-full md:w-1/3">
            <KanbanColumn
              title="Done"
              status="done"
              tasks={byStatus.done}
              members={project.members || []}
              projectId={project.id}
              isEmployee={isEmployee}
              isManager={isManager}
              isMemberOfProject={isMemberOfProject}
              showAddTask={false}
              onToggleAddTask={() => {}}
            />
          </div>
        </div>
      </DragDropContext>
    </div>
  );
}

// ---- members tab ----
function MembersTab({ project }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: allUsers = [], isLoading: usersLoading } = useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const res = await api.get('/api/users');
      return res.data?.data || [];
    },
  });

  const addMember = useMutation({
    mutationFn: (userId) => api.post(`/api/projects/${project.id}/members`, { userId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });

  const removeMember = useMutation({
    mutationFn: (userId) => api.delete(`/api/projects/${project.id}/members/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });

  const memberIds = useMemo(
    () => new Set((project.members || []).map((m) => m.userId)),
    [project.members]
  );

  const filteredNonMembers = allUsers.filter(
    (u) =>
      !memberIds.has(u.id) &&
      (u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="p-4 space-y-6">
      {/* Current members */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Current Members ({(project.members || []).length})
        </h3>
        {(project.members || []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
            <Users className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-400">No members yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {(project.members || []).map((member, idx) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Avatar name={member.user?.name} size="md" colorIndex={idx} />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{member.user?.name || 'Unknown'}</p>
                    <p className="text-xs text-gray-500">{member.user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <RoleBadge role={member.user?.role} />
                  <button
                    onClick={() => removeMember.mutate(member.userId)}
                    disabled={removeMember.isPending}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                    title="Remove from project"
                  >
                    <UserMinus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add member */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Add Member</h3>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search team members..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {usersLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((n) => <Skeleton key={n} className="h-14 rounded-lg" />)}
          </div>
        ) : filteredNonMembers.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            {search ? 'No matching team members found.' : 'All team members are already in this project.'}
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredNonMembers.map((user, idx) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Avatar name={user.name} size="md" colorIndex={idx} />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <RoleBadge role={user.role} />
                  <button
                    onClick={() => addMember.mutate(user.id)}
                    disabled={addMember.isPending}
                    className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                    title="Add to project"
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- project detail panel ----
function ProjectDetail({ project, isEmployee, isManager, userId }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('tasks');
  const [showEditModal, setShowEditModal] = useState(false);

  const updateProject = useMutation({
    mutationFn: (updates) => api.put(`/api/projects/${project.id}`, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });

  const deleteProject = useMutation({
    mutationFn: () => api.delete(`/api/projects/${project.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const handleSaveName = async (name) => {
    await updateProject.mutateAsync({ name });
  };

  const handleArchiveToggle = async () => {
    await updateProject.mutateAsync({ status: project.archived ? 'active' : 'archived' });
  };

  const burnPct =
    project.budget && project.totalHours != null
      ? Math.round((project.totalHours / project.budget) * 100)
      : 0;

  const shownAvatars = (project.members || []).slice(0, 5);
  const extraMembers = (project.members || []).length - shownAvatars.length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-200 bg-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 group">
            <InlineEditName
              value={project.name}
              onSave={handleSaveName}
              disabled={!isManager}
            />
            {project.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{project.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusBadge archived={project.archived} />
            {isManager && (
              <>
                <button
                  onClick={() => setShowEditModal(true)}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  title="Edit project details"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={handleArchiveToggle}
                  disabled={updateProject.isPending}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  title={project.archived ? 'Unarchive project' : 'Archive project'}
                >
                  {project.archived ? (
                    <ArchiveRestore className="w-4 h-4" />
                  ) : (
                    <Archive className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('PERMANENTLY DELETE this project and all its data (tasks, logs, invoices)? This cannot be undone.')) {
                      deleteProject.mutate();
                    }
                  }}
                  disabled={deleteProject.isPending}
                  className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  title="Permanently delete project"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Task Completion</span>
              <CheckCircle2 className="w-4 h-4 text-green-500" />
            </div>
            <div className="flex items-end justify-between">
              <div>
                <span className="text-2xl font-bold text-gray-900">{project.stats?.completedTasks || 0}</span>
                <span className="text-gray-400 text-sm ml-1">/ {project.stats?.totalTasks || 0} tasks</span>
              </div>
              <span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                {project.stats?.percentDone || 0}%
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3 overflow-hidden">
              <div 
                className="bg-green-500 h-full rounded-full transition-all duration-500" 
                style={{ width: `${project.stats?.percentDone || 0}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Budget Utilization</span>
              <Activity className="w-4 h-4 text-blue-500" />
            </div>
            <div className="flex items-end justify-between">
              <div>
                <span className="text-2xl font-bold text-gray-900">{fmtHours(project.totalHours)}</span>
                <span className="text-gray-400 text-sm ml-1">/ {project.budget || 0}h limit</span>
              </div>
              <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${burnPct >= 90 ? 'text-red-600 bg-red-50' : 'text-blue-600 bg-blue-50'}`}>
                {burnPct}%
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${burnPct >= 90 ? 'bg-red-500' : 'bg-blue-500'}`} 
                style={{ width: `${Math.min(burnPct, 100)}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Project Members</span>
              <Users className="w-4 h-4 text-purple-500" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex -space-x-2">
                {shownAvatars.map((m, i) => (
                  <Avatar key={m.id} name={m.user?.name} size="md" colorIndex={i} className="border-2 border-white shadow-sm" />
                ))}
                {extraMembers > 0 && (
                  <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-gray-500 z-10">
                    +{extraMembers}
                  </div>
                )}
              </div>
              <div className="text-right">
                <span className="block text-2xl font-bold text-gray-900">{(project.members || []).length}</span>
                <span className="text-xs text-gray-400">Total contributors</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tasks in Progress */}
        {project.activeTrackers?.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">Tasks In Progress</h3>
              <span className="ml-auto text-xs text-gray-400 font-medium">Real-time status</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.activeTrackers.map((tracker) => (
                <div key={tracker.userId} className="flex flex-col p-4 rounded-xl border border-green-100 bg-green-50/30 shadow-sm relative overflow-hidden">
                  <div className="flex items-start gap-3 relative z-10">
                    <Avatar name={tracker.userName} size="md" className="ring-2 ring-white" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate">{tracker.userName}</div>
                      <div className="text-[11px] font-medium text-gray-500 line-clamp-1 flex items-center gap-1 mt-0.5">
                        <Play className="w-2.5 h-2.5 text-green-600 fill-green-600" />
                        {tracker.taskTitle || 'Working on Project'}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-green-700 bg-white px-2 py-1 rounded-md border border-green-100 shadow-sm">
                      <Clock className="w-3 h-3" />
                      <span>Since {new Date(tracker.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-ping" />
                      <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider">Live</span>
                    </div>
                  </div>
                  <div className="absolute -right-2 -bottom-2 opacity-[0.03]">
                    <Activity className="w-16 h-16 text-green-900" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mt-4 border-b border-gray-200 -mb-px">
          {['tasks', ...(isManager ? ['members'] : [])].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'tasks' && (
          <TasksTab
            project={project}
            isEmployee={isEmployee}
            isManager={isManager}
            userId={userId}
          />
        )}
        {activeTab === 'members' && isManager && <MembersTab project={project} />}
      </div>

      {/* Edit modal */}
      {showEditModal && (
        <ProjectFormModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            queryClient.invalidateQueries({ queryKey: ['projects'] });
          }}
        />
      )}
    </div>
  );
}

// ---- project list item ----
function ProjectListItem({ project, selected, onClick }) {
  const burnPct =
    project.budget && project.totalHours != null
      ? Math.round((project.totalHours / project.budget) * 100)
      : 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-150 border ${
        selected
          ? 'bg-blue-50 border-blue-200 border-l-4 border-l-blue-600'
          : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
      }`}
    >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-sm font-medium truncate ${selected ? 'text-blue-800' : 'text-gray-800'}`}>
            {project.name}
          </span>
          {project.archived && (
            <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500 uppercase tracking-wider">
              Archived
            </span>
          )}
        </div>
        <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-colors ${selected ? 'text-blue-500' : 'text-gray-400'}`} />
      <div className="flex items-center gap-3 mt-2">
        <span className="flex items-center gap-1 text-[10px] text-gray-400 font-medium">
          <Users className="w-2.5 h-2.5" />
          {(project.members || []).length}
        </span>
        <span className="flex items-center gap-1 text-[10px] text-gray-400 font-medium">
          <CheckCircle2 className="w-2.5 h-2.5" />
          {project._count?.tasks || 0}
        </span>
        <span className="ml-auto text-[10px] font-bold text-gray-500">
          {fmtHours(project.totalHours)} logged
        </span>
      </div>
      {/* Mini Progress Bar */}
      <div className="mt-2 w-full bg-gray-100 rounded-full h-1 overflow-hidden">
        <div 
          className="bg-blue-600 h-full rounded-full transition-all duration-300"
          style={{ width: `${project.stats?.percentDone || 0}%` }}
        />
      </div>
    </button>
  );
}

// ---- main page ----
export default function Projects() {
  const queryClient = useQueryClient();
  const { user, isManager } = useAuth();
  const isEmployee = user?.role === 'EMPLOYEE';
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const res = await api.get('/api/projects');
      const list = res.data?.data || [];
      return list.map((p) => ({ ...p, archived: p.status === 'archived' }));
    },
  });

  const allProjects = data || [];

  const displayed = useMemo(() => {
    const byTab = tab === 'active' ? allProjects.filter((p) => !p.archived) : allProjects.filter((p) => p.archived);
    if (!search.trim()) return byTab;
    const q = search.toLowerCase();
    return byTab.filter((p) => p.name.toLowerCase().includes(q));
  }, [allProjects, tab, search]);

  const selectedProject = useMemo(
    () => allProjects.find((p) => p.id === selectedId) || null,
    [allProjects, selectedId]
  );

  // Auto-select first project when list changes
  useEffect(() => {
    if (!selectedId && displayed.length > 0) {
      setSelectedId(displayed[0].id);
    }
    if (selectedId && !allProjects.find((p) => p.id === selectedId)) {
      setSelectedId(displayed[0]?.id || null);
    }
  }, [displayed]);

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <FolderOpen className="w-10 h-10 text-gray-300" />
        <p className="text-red-600 font-medium">Failed to load projects.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Left sidebar */}
      <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
        {/* Sidebar header */}
        <div className="px-4 py-4 border-b border-gray-200 space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-base font-semibold text-gray-900">Projects</h1>
            {isManager && (
              <button
                onClick={() => setShowNewModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                New
              </button>
            )}
          </div>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search projects..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {/* Tabs */}
          <div className="flex gap-1 p-0.5 bg-gray-200 rounded-lg">
            {['active', 'archived'].map((t) => (
              <button
                key={t}
                onClick={() => {
                  setTab(t);
                  setSelectedId(null);
                }}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${
                  tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Project list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {isLoading ? (
            <>
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-1.5 rounded-full" />
                </div>
              ))}
            </>
          ) : displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <FolderOpen className="w-8 h-8 text-gray-300" />
              <p className="text-xs text-gray-400 text-center">
                {search ? 'No projects match your search.' : `No ${tab} projects yet.`}
              </p>
              {tab === 'active' && !search && isManager && (
                <button
                  onClick={() => setShowNewModal(true)}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Create your first project
                </button>
              )}
            </div>
          ) : (
            displayed.map((project) => (
              <ProjectListItem
                key={project.id}
                project={project}
                selected={project.id === selectedId}
                onClick={() => setSelectedId(project.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Right content panel */}
      <div className="flex-1 min-w-0 bg-white flex flex-col">
        {selectedProject ? (
          <ProjectDetail
            key={selectedProject.id}
            project={selectedProject}
            isEmployee={isEmployee}
            isManager={isManager}
            userId={user?.id}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-8">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
              <FolderOpen className="w-8 h-8 text-gray-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-700">Select a project</h3>
              <p className="text-sm text-gray-400 mt-1">
                Choose a project from the list to view details, tasks, and members.
              </p>
            </div>
            {isManager && (
              <button
                onClick={() => setShowNewModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Project
              </button>
            )}
          </div>
        )}
      </div>

      {/* New project modal */}
      {showNewModal && (
        <ProjectFormModal
          onClose={() => setShowNewModal(false)}
          onSuccess={() => {
            setShowNewModal(false);
            queryClient.invalidateQueries({ queryKey: ['projects'] });
          }}
        />
      )}
    </div>
  );
}
